/**
 * Merchant Configurations Types
 *
 * TypeScript types for the Return App configuration feature.
 * Defines the structure of the merchant_configurations table and flow_config JSONB.
 */

// Multilingual text structure - supports any language codes from merchant's supported_languages
// Keys are ISO language codes (e.g., 'en', 'de', 'fr'), values are the translated text
export type MultilingualText = Record<string, string>;

// Legacy alias for backwards compatibility
export type BilingualText = MultilingualText;

// Valid section types
export type SectionType = 'photo_upload' | 'question_set' | 'completion';

// Valid question types
export type QuestionType = 'radio' | 'checkbox' | 'text' | 'textarea';

// Valid option actions
export type OptionAction = 'proceed' | 'block' | 'require_details';

// Valid photo requirement modes for item photos
export type PhotoMode = 'optional' | 'required' | 'required_if_issues';

// Valid pagination modes for item questions
export type PaginationMode = 'one_per_page' | 'accordion' | 'all_at_once';

// Details config for options with action="require_details"
export interface DetailsConfig {
  field_name: string;
  placeholder?: BilingualText;  // Optional; omitted when empty in every language
  required: boolean;
}

// Option structure for radio/checkbox questions
export interface QuestionOption {
  value: string;
  label: BilingualText;
  action: OptionAction;
  blocking_message?: BilingualText;  // Required if action="block"
  details_config?: DetailsConfig;     // Required if action="require_details"
}

// Question structure
export interface Question {
  id: string;
  order: number;
  preamble?: BilingualText | null;
  text: BilingualText;
  type: QuestionType;
  required: boolean;
  options?: QuestionOption[];  // Required for radio/checkbox types
}

// Item photos configuration for item-scope question sets
export interface ItemPhotosConfig {
  enabled: boolean;
  mode: PhotoMode;
  min_photos: number;
  max_photos: number;
  instructions?: BilingualText;
}

// Base section interface
export interface BaseSection {
  id: string;
  type: SectionType;
  enabled: boolean;
  order: number;
  config: PhotoUploadConfig | QuestionSetConfig | CompletionConfig;
}

// Photo upload section config
export interface PhotoUploadConfig {
  scope: 'booking';
  header_text?: BilingualText;
  instructions?: BilingualText;
  show_instructions?: boolean;
  min_photos?: number;  // Default: 0
  max_photos?: number;  // Default: 10, max: 50
}

// Question set section config (booking or item scope)
export interface QuestionSetConfig {
  scope: 'booking' | 'item';
  header_text?: BilingualText;
  instructions?: BilingualText;
  show_instructions?: boolean;
  questions: Question[];
  // Item-scope only properties
  mode?: 'per_inventory_item';
  pagination?: PaginationMode;
  skip_if_no_items?: boolean;
  show_item_count?: boolean;
  item_photos_config?: ItemPhotosConfig;
}

// Instruction box for completion section
export interface InstructionBox {
  order: number;
  title: BilingualText;
  text: BilingualText;
  icon: string;   // e.g., "lock", "check", "info"
  color: string;  // e.g., "green", "blue", "yellow"
  /**
   * Optional per-location visibility filter.
   *  - undefined / missing → box shows on every location (default).
   *  - non-empty array     → box shows only for bookings whose location_id is in the list.
   *  - empty array         → never written; the editor's serializer drops the key when no
   *                          locations are selected, keeping configs minimal.
   */
  location_ids?: string[];
}

// Review request configuration
export interface ReviewRequest {
  enabled: boolean;
  text?: BilingualText;
  button_text?: BilingualText;
  use_location_specific_url?: boolean;
}

// Completion section config
export interface CompletionConfig {
  header_text: BilingualText;
  message: BilingualText;
  review_request: ReviewRequest;
  instruction_boxes: InstructionBox[];  // Max 3
}

// Section type with specific config
export interface PhotoUploadSection extends BaseSection {
  type: 'photo_upload';
  config: PhotoUploadConfig;
}

export interface QuestionSetSection extends BaseSection {
  type: 'question_set';
  config: QuestionSetConfig;
}

export interface CompletionSection extends BaseSection {
  type: 'completion';
  config: CompletionConfig;
}

export type Section = PhotoUploadSection | QuestionSetSection | CompletionSection;

// Flow config root structure
export interface FlowConfig {
  sections: Section[];
}

// Main merchant configuration interface
export interface MerchantConfiguration {
  id: string;
  merchant_id: string;
  flow_config: FlowConfig;
  auto_complete_booking: boolean;
  allow_return_updates: boolean;
  created_at: string;
  updated_at: string;
}

// For creating/updating configurations (omit server-generated fields)
export interface MerchantConfigurationInput {
  merchant_id: string;
  flow_config: FlowConfig;
  auto_complete_booking?: boolean;
  allow_return_updates?: boolean;
}

// Available languages with display names
export const AVAILABLE_LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'it', name: 'Italian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'fi', name: 'Finnish' },
];

// Helper to get language name from code
export function getLanguageName(code: string): string {
  return AVAILABLE_LANGUAGES.find(l => l.code === code)?.name || code.toUpperCase();
}

// Validation constants
export const VALID_SECTION_TYPES: SectionType[] = ['photo_upload', 'question_set', 'completion'];
export const VALID_QUESTION_TYPES: QuestionType[] = ['radio', 'checkbox', 'text', 'textarea'];
export const VALID_ACTIONS: OptionAction[] = ['proceed', 'block', 'require_details'];
export const VALID_PHOTO_MODES: PhotoMode[] = ['optional', 'required', 'required_if_issues'];
export const VALID_PAGINATION_MODES: PaginationMode[] = ['one_per_page', 'accordion', 'all_at_once'];

export const MAX_PHOTOS_LIMIT = 50;
export const MAX_QUESTIONS_PER_SECTION = 5;
export const MAX_INSTRUCTION_BOXES = 3;

// Available icons for instruction boxes
export const AVAILABLE_ICONS = ['lock', 'check', 'info', 'alert', 'bike', 'key', 'phone', 'email', 'clock', 'map'];

// Available colors for instruction boxes
export const AVAILABLE_COLORS = ['green', 'blue', 'yellow', 'red', 'gray', 'orange', 'purple'];

// Default minimal configuration (completion section only)
export const DEFAULT_MINIMAL_CONFIG: FlowConfig = {
  sections: [
    {
      id: 'thank_you',
      type: 'completion',
      enabled: true,
      order: 1,
      config: {
        header_text: { de: 'Vielen Dank!', en: 'Thank You!' },
        message: { de: 'Ihre Rückgabe wurde erfolgreich erfasst.', en: 'Your return has been successfully recorded.' },
        review_request: { enabled: false },
        instruction_boxes: [],
      },
    },
  ],
};

// Helper function to create an empty multilingual text object.
// Language keys are only added when there is actual content — the return-app
// contract forbids empty-string language keys, so none are seeded here.
export function createEmptyMultilingualText(_languages: string[] = []): MultilingualText {
  return {};
}

// Legacy alias
export function createEmptyBilingualText(): BilingualText {
  return {};
}

// Helper function to create a new photo upload section
export function createPhotoUploadSection(order: number, languages: string[] = ['en', 'de']): PhotoUploadSection {
  const headerText = createEmptyMultilingualText(languages);
  const instructions = createEmptyMultilingualText(languages);
  if (languages.includes('de')) { headerText.de = 'Fotos hochladen'; instructions.de = 'Bitte laden Sie Fotos hoch.'; }
  if (languages.includes('en')) { headerText.en = 'Upload Photos'; instructions.en = 'Please upload photos.'; }

  return {
    id: `photos_${Date.now()}`,
    type: 'photo_upload',
    enabled: true,
    order,
    config: {
      scope: 'booking',
      header_text: headerText,
      instructions: instructions,
      show_instructions: true,
      min_photos: 0,
      max_photos: 10,
    },
  };
}

// Helper function to create a new question set section (booking scope)
export function createBookingQuestionSetSection(order: number, languages: string[] = ['en', 'de']): QuestionSetSection {
  const headerText = createEmptyMultilingualText(languages);
  const instructions = createEmptyMultilingualText(languages);
  if (languages.includes('de')) { headerText.de = 'Allgemeine Fragen'; instructions.de = 'Bitte beantworten Sie die folgenden Fragen.'; }
  if (languages.includes('en')) { headerText.en = 'General Questions'; instructions.en = 'Please answer the following questions.'; }

  return {
    id: `booking_questions_${Date.now()}`,
    type: 'question_set',
    enabled: true,
    order,
    config: {
      scope: 'booking',
      header_text: headerText,
      instructions: instructions,
      show_instructions: true,
      questions: [],
    },
  };
}

// Helper function to create a new question set section (item scope)
export function createItemQuestionSetSection(order: number, languages: string[] = ['en', 'de']): QuestionSetSection {
  const headerText = createEmptyMultilingualText(languages);
  const instructions = createEmptyMultilingualText(languages);
  if (languages.includes('de')) { headerText.de = 'Zustandsprüfung: {item_name}'; instructions.de = 'Bitte prüfen Sie diesen Artikel.'; }
  if (languages.includes('en')) { headerText.en = 'Condition Check: {item_name}'; instructions.en = 'Please check this item.'; }

  return {
    id: `item_questions_${Date.now()}`,
    type: 'question_set',
    enabled: true,
    order,
    config: {
      scope: 'item',
      mode: 'per_inventory_item',
      pagination: 'one_per_page',
      header_text: headerText,
      instructions: instructions,
      show_instructions: true,
      skip_if_no_items: true,
      show_item_count: true,
      questions: [],
      item_photos_config: {
        enabled: false,
        mode: 'optional',
        min_photos: 0,
        max_photos: 5,
      },
    },
  };
}

// Helper function to create a new completion section
export function createCompletionSection(order: number, languages: string[] = ['en', 'de']): CompletionSection {
  const headerText = createEmptyMultilingualText(languages);
  const message = createEmptyMultilingualText(languages);
  if (languages.includes('de')) { headerText.de = 'Vielen Dank!'; message.de = 'Ihre Rückgabe wurde erfolgreich erfasst.'; }
  if (languages.includes('en')) { headerText.en = 'Thank You!'; message.en = 'Your return has been successfully recorded.'; }

  return {
    id: `completion_${Date.now()}`,
    type: 'completion',
    enabled: true,
    order,
    config: {
      header_text: headerText,
      message: message,
      review_request: { enabled: false },
      instruction_boxes: [],
    },
  };
}

// Helper function to create a new question
export function createQuestion(order: number, languages: string[] = ['en', 'de']): Question {
  const text = createEmptyMultilingualText(languages);
  const yesLabel = createEmptyMultilingualText(languages);
  const noLabel = createEmptyMultilingualText(languages);
  if (languages.includes('de')) { yesLabel.de = 'Ja'; noLabel.de = 'Nein'; }
  if (languages.includes('en')) { yesLabel.en = 'Yes'; noLabel.en = 'No'; }

  return {
    id: `question_${Date.now()}`,
    order,
    text,
    type: 'radio',
    required: true,
    options: [
      {
        value: 'yes',
        label: yesLabel,
        action: 'proceed',
      },
      {
        value: 'no',
        label: noLabel,
        action: 'proceed',
      },
    ],
  };
}

// Helper function to create a new option
export function createOption(languages: string[] = ['en', 'de']): QuestionOption {
  return {
    value: '',
    label: createEmptyMultilingualText(languages),
    action: 'proceed',
  };
}

// Helper function to create an instruction box
export function createInstructionBox(order: number, languages: string[] = ['en', 'de']): InstructionBox {
  return {
    order,
    title: createEmptyMultilingualText(languages),
    text: createEmptyMultilingualText(languages),
    icon: 'info',
    color: 'blue',
  };
}
